import type { Account } from "./types";
import { round2 } from "./format";
import { RETIRE_SPLIT_RE, isCalculated401k } from "./retirement";

/**
 * Display-time account transforms. The store keeps each 401k split into its
 * calculated Roth and Traditional halves (so the Net Worth page can chart
 * Roth-vs-non-Roth), but everywhere else the app shows ONE balance per 401k.
 * `aggregateAccounts` collapses each calculated Roth/Traditional pair into a
 * single synthetic account; use it for any account list/detail that should
 * present the combined 401k. The synthetic id carries a "(aggregated)" marker
 * so both the Accounts list and the detail route resolve to the same record.
 */

/** Stable synthetic id for an aggregated 401k, distinct from any real account. */
export function aggregated401kId(institution: string, owner: string, prefix: string): string {
  return `${institution}|${owner}|${prefix} (aggregated)`.toLowerCase().replace(/ /g, "-");
}

/**
 * Collapse each calculated 401k Roth+Traditional pair into one combined
 * account (summed balances, latest asOf, active if either half is). All other
 * accounts pass through untouched, preserving order (the aggregate takes the
 * position of the first half encountered).
 *
 * The aggregate also inherits DISPLAY history from the plan's retired
 * predecessor accounts (the pre-split "<plan>" total and the per-source
 * "<plan> - PRE-TAX"-style sub-accounts): their balance points from before the
 * calculated halves' history begins are summed in, so the Accounts pages show
 * the plan's full multi-year balance curve. Display-only — the stored splits
 * are untouched and net worth never reads these synthetic records, so no
 * historical sum changes.
 */
export function aggregateAccounts(accounts: Account[]): Account[] {
  const pairs = new Map<string, Account[]>();
  for (const a of accounts) {
    if (!isCalculated401k(a)) continue;
    const prefix = a.name.match(RETIRE_SPLIT_RE)![1];
    const key = `${a.institution}|${a.owner}|${prefix}`;
    (pairs.get(key) ?? pairs.set(key, []).get(key)!).push(a);
  }

  const combined = new Map<string, Account>();
  for (const [key, members] of pairs) {
    const [institution, owner, prefix] = key.split("|");
    const balances: Record<string, number> = {};
    for (const m of members) {
      for (const [month, v] of Object.entries(m.balances)) {
        balances[month] = round2((balances[month] ?? 0) + v);
      }
    }
    // Splice in the retired predecessors' history, but only for dates before
    // the calculated halves start — never overlapping (which would double-
    // count a date observed by both generations).
    const calcStart = Object.keys(balances).sort()[0] ?? "9999-99-99";
    for (const a of accounts) {
      if (isCalculated401k(a) || a.active !== false) continue;
      if (a.type !== "Retirement" || a.institution !== institution || a.owner !== owner) continue;
      if (a.name !== prefix && !a.name.startsWith(`${prefix} - `)) continue;
      for (const [month, v] of Object.entries(a.balances)) {
        if (month >= calcStart) continue;
        balances[month] = round2((balances[month] ?? 0) + v);
      }
    }
    const asOf = members.map((m) => m.asOf).filter(Boolean).sort().pop();
    combined.set(key, {
      id: aggregated401kId(institution, owner, prefix),
      institution,
      owner,
      type: "Retirement",
      name: prefix,
      isLiability: false,
      balances,
      asOf,
      active: members.some((m) => m.active !== false),
    });
  }

  const out: Account[] = [];
  const emitted = new Set<string>();
  for (const a of accounts) {
    if (isCalculated401k(a)) {
      const prefix = a.name.match(RETIRE_SPLIT_RE)![1];
      const key = `${a.institution}|${a.owner}|${prefix}`;
      if (!emitted.has(key)) {
        out.push(combined.get(key)!);
        emitted.add(key);
      }
      continue;
    }
    out.push(a);
  }
  return out;
}
